"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppPropsProvider } from "./_apps/AppPropsContext";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { TurnrSnackbarAlert } from "../components/snackbars/TurnrSnackbar.jsx";
import { getDeployedHost, getServiceError } from "rylak-common";
import { smartHrefOnClick } from "../utils";
import { TurnrSnackbarsHandler } from "../components/snackbars/TurnrSnackbarsHandler.jsx";
import { interFont, robotoFont, sharpGrotesk15Font, sharpGrotesk20Font, sharpGrotesk25Font } from "./_apps/fonts.jsx";
import classnames from "classnames";
import styles from "./ClientLayout.module.scss";
import _ from "lodash";

/**
 * Backoffice is complicated stuff so we generally want the top layer layout to be client side only rendered. And we choose to ignore some of the server-side rendering optimization
 */
export default function RootClientLayout({ children }) {
    const router = useRouter();
    const pathname = usePathname();

    let [snackbarsData, setSnackbarsData] = useState([]);

    const openSnackbar = useCallback(
        ({ Component, componentProps, onClose, ...rest }) => {
            snackbarsData.push({
                Component,
                componentProps,
                onClose,
                id: _.uniqueId(),
                ...rest,
            });
            setSnackbarsData(snackbarsData.slice());
        },
        [snackbarsData, setSnackbarsData],
    );

    const openSnackbarError = useCallback(
        ({ serviceData, title, content }) => {
            openSnackbar({
                Component: TurnrSnackbarAlert,
                componentProps: {
                    title: title,
                    content: content || getServiceError(serviceData),
                    type: "ERROR",
                    traceId: serviceData?.traceId,
                },
                openTime: 15000,
            });
        },
        [openSnackbar],
    );

    const openSnackbarSuccess = useCallback(
        ({ title, content }) => {
            openSnackbar({
                Component: TurnrSnackbarAlert,
                componentProps: {
                    title: title,
                    content: content,
                    type: "SUCCESS",
                },
            });
        },
        [openSnackbar],
    );

    const removeSnackbarData = useCallback(
        ({ id }) => {
            const index = snackbarsData.findIndex((snackbarData) => {
                return snackbarData.id === id;
            });
            index >= 0 && snackbarsData.splice(index, 1);
            setSnackbarsData(snackbarsData.slice());
        },
        [snackbarsData, setSnackbarsData],
    );

    const searchParams = useSearchParams();
    const queryParams = Object.fromEntries(searchParams.entries());
    const pathParams = useParams();

    const [reloadPageIndex, setReloadPageIndex] = useState(0);

    const reloadPage = useCallback(() => {
        setReloadPageIndex((reloadPageIndex) => reloadPageIndex + 1);
    }, [setReloadPageIndex]);

    const openPage = useCallback(
        (e, href) => {
            smartHrefOnClick(e, router, href)?.then(() => {
                reloadPage();
            });
        },
        [reloadPage],
    );

    const system = {
        ...getDeployedHost(),
        fetch: (url, data) => {
            if (data.method === "GET") {
                return fetch(url, data).catch((error) => {
                    if (error instanceof TypeError) {
                        return fetch(url, data).catch((error) => {
                            if (error instanceof TypeError) {
                                return fetch(url, data);
                            } else {
                                return error;
                            }
                        });
                    } else {
                        return error;
                    }
                });
            }
            return fetch(url, data);
        },
    };

    const [currentDateClient, setCurrentDateClient] = useState(null);
    useEffect(() => {
        // To avoid hydration errors
        setCurrentDateClient(new Date());
    }, []);

    const appProps = useMemo(() => {
        return {
            //For all page
            currentDateClient,
            system,
            router,
            pathname,
            openSnackbar,
            openSnackbarError,
            openSnackbarSuccess,
            queryParams,
            pathParams,
            openPage,
            reloadPage,
            reloadPageIndex,
        };
    }, [currentDateClient, system, router, pathname, openSnackbar, openSnackbarError, openSnackbarSuccess, queryParams, pathParams, openPage, reloadPage, reloadPageIndex]);

    useEffect(() => {
        // Special treatment to make sure body gets the correct font
        document.body.classList.add(interFont.variable);
        document.body.style.fontFamily = interFont.style.fontFamily;
    }, []);

    return (
        // <OidcAuthProvider>
        <AppPropsProvider appProps={appProps}>
            <div className={classnames(styles["app-wrapper"], robotoFont.variable, interFont.variable, sharpGrotesk20Font.variable, sharpGrotesk15Font.variable, sharpGrotesk25Font.variable)}>
                <TurnrSnackbarsHandler snackbarsData={snackbarsData} removeSnackbarData={removeSnackbarData} />
                {children}
            </div>
        </AppPropsProvider>
        // </OidcAuthProvider>
    );
}
